'use client'

import { useState, useCallback, useEffect } from 'react'
import { NetworkTreeNode } from './types'
import { shouldAutoExpand, canBeCollapsed } from './helpers'

export function useExpansion(
  data: NetworkTreeNode[],
  disabledNodeIds: Set<string> = new Set(),
  searchTerm: string = ''
) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() => {
    const nodesToExpand = data
      .filter(node => shouldAutoExpand(node, disabledNodeIds, data, searchTerm))
      .map(node => node.network.id)

    return new Set(nodesToExpand)
  })

  // Recalculate expansion when search term changes
  useEffect(() => {
    const nodesToExpand = data
      .filter(node => shouldAutoExpand(node, disabledNodeIds, data, searchTerm))
      .map(node => node.network.id)

    setExpandedNodes(new Set(nodesToExpand))
  }, [searchTerm])

  const toggleNode = useCallback(
    (nodeId: string) => {
      if (disabledNodeIds.has(nodeId)) {
        return
      }

      const node = data.find(n => n.network.id === nodeId)
      const isCurrentlyExpanded = expandedNodes.has(nodeId)

      if (node && isCurrentlyExpanded && !canBeCollapsed(node, data)) {
        return
      }

      setExpandedNodes(current => {
        const next = new Set(current)
        if (next.has(nodeId)) {
          next.delete(nodeId)
        } else {
          next.add(nodeId)
        }
        return next
      })
    },
    [disabledNodeIds, data, expandedNodes]
  )

  return {
    expandedNodes,
    toggleNode
  }
}
